import React, { FC } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import dayjs from "dayjs";
import { SiteOfficeClosure } from "@/domain/site-office-hours.interface";
import { SkeletonClosureSection } from "./skeletons";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";

const parseNaiveDatetime = (iso: string) => dayjs(iso.replace("Z", ""));

type Props = {
  closure: SiteOfficeClosure | null | undefined;
  isLoading: boolean;
  onEditClick: () => void;
};

export const ClosureSection: FC<Props> = ({
  closure,
  isLoading,
  onEditClick,
}) => {
  if (isLoading) {
    return <SkeletonClosureSection />;
  }

  const isActiveClosure = !!closure;

  const borderColor = isActiveClosure ? "#EF9A9A" : "#EF9A9A";

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor,
        borderRadius: "8px",
        bgcolor: isActiveClosure ? "#FEEBEE" : "#FEEBEE",
        overflow: "hidden",
      }}
    >
      {/* Top row: icon + text + edit button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#FECDD2",
              borderRadius: "50%",
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            <WarningAmberIcon
              sx={{
                color: isActiveClosure ? "error.main" : "error.main",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="bodyMedium" fontWeight={600}>
              Emergency / Holiday Closure
            </Typography>
            {isActiveClosure && (
              <Typography variant="bodyMedium" color="icon.dark">
                This message will be visible to participants according to the
                scheduled visibility settings.
              </Typography>
            )}
          </Box>
        </Box>

        <Button
          variant="contained"
          color="error"
          startIcon={<NoteAltOutlinedIcon />}
          onClick={onEditClick}
          disabled={isLoading}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          EDIT
        </Button>
      </Box>

      {/* Divider + reason row */}
      <Divider sx={{ borderColor, ml: 1, mr: 1 }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="bodyMedium" fontWeight={600}>
          {isActiveClosure
            ? `Reason: ${closure.reason}`
            : "Reason: No active closure"}
        </Typography>
        {isActiveClosure && (
          <>
            <Typography variant="bodyMedium">
              <strong>Additional notes:</strong> {closure.notes?.trim() || "N/A"}
            </Typography>
            <Typography variant="bodyMedium">
              <strong>Scheduled visibility:</strong>{" "}
              {parseNaiveDatetime(closure.visibilityStartDate).format(
                "MMM D, YYYY h:mm A"
              )}{" "}
              —{" "}
              {parseNaiveDatetime(closure.visibilityEndDate).format(
                "MMM D, YYYY h:mm A"
              )}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
